import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../context/ToastContext';
import { OrderStatus } from '../../../../../enums/orders';
import useAxiosHook from '../../../../../hooks/useAxiosHook';
import usePrevious from '../../../../../hooks/usePrevious';
import { Numeric } from '../../../../../types/general';
import {
  errorToast,
  successToast,
} from '../../../../../utils/helpers/primereact';
import { Order } from '../../Orders.functions';
import { generateErrorMsg, toApiData } from './CancelOrderDialog.functions';

type Props = {
  visible: boolean;
  data: Order[];
  onHide: () => void;
  reloadOrders: () => void;
  setSelectionMultiple: Dispatch<SetStateAction<any[]>>;
};

function CancelOrderDialog({
  visible,
  data,
  onHide,
  reloadOrders,
  setSelectionMultiple,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { toastRef } = useContext(ToastContext);
  const {
    data: cancelData,
    error: cancelError,
    reload: cancelReload,
    isLoading: isCancelLoading,
  } = useAxiosHook<undefined, { error?: string }>();

  function handleCancelOrder(orderId: Numeric) {
    if (!orderId) {
      return;
    }
    cancelReload({ url: `orders/${orderId}/cancel`, method: 'POST' });
  }

  const prevCancelData = usePrevious(cancelData);
  const prevCancelError = usePrevious(cancelError);

  useEffect(() => {
    if (!cancelData || cancelData === prevCancelData) {
      return;
    }

    if (toastRef?.current) {
      successToast(
        toastRef,
        t('Success'),
        data?.length > 1
          ? t('Orders successfully cancelled.')
          : t('Order {{serialNo}} was successfully cancelled.', {
              serialNo: data[0]?.seriski_broj ?? '',
            })
      );
    }
    onHide();
    reloadOrders();
    setSelectionMultiple([]);
  }, [
    data,
    cancelData,
    prevCancelData,
    onHide,
    reloadOrders,
    setSelectionMultiple,
    t,
    toastRef,
  ]);

  useEffect(() => {
    if (!cancelError || cancelError === prevCancelError) {
      return;
    }
    const errText = cancelError?.response?.data?.error;

    if (toastRef?.current) {
      errorToast(
        toastRef,
        t('Error'),
        data?.length > 1
          ? t('Failed group cancelling. {{errorMessage}}', {
              errorMessage: errText ? generateErrorMsg(t, errText) : '',
            })
          : t('Canceling order {{serialNo}} failed. {{errorMessage}}.', {
              serialNo: data[0]?.seriski_broj ?? '',
              errorMessage: errText ? generateErrorMsg(t, errText) : '',
            })
      );
    }

    onHide();
  }, [data, cancelError, onHide, prevCancelError, t, toastRef]);

  function handleCancelSubmission() {
    if (!data?.length) {
      return;
    }
    const cancellableData = toApiData(data);
    cancellableData.forEach((o) => handleCancelOrder(o.id));
  }

  const nonCanceledOrders = useMemo(
    () => data?.filter((o) => o?.status_id >= OrderStatus.Cancelled),
    [data]
  );

  const ordersForCancel = useMemo(
    () => data?.filter((o) => o?.status_id < OrderStatus.Cancelled),
    [data]
  );

  const dialogFooter = (
    <div className="p-pt-4">
      <Button
        type="button"
        label={t('Close')}
        onClick={() => onHide()}
        className="p-button-secondary p-button-text"
      />

      <Button
        type="button"
        label={isCancelLoading ? t('Cancelling...') : t('Cancel')}
        disabled={isCancelLoading || ordersForCancel.length === 0}
        onClick={() => {
          handleCancelSubmission();
        }}
        className="p-button-danger"
        data-cy="submit-btn"
      />
    </div>
  );

  return (
    <Dialog
      header={data?.length > 1 ? t('Cancel orders') : t('Cancel order')}
      footer={dialogFooter}
      visible={visible}
      resizable={false}
      onHide={onHide}
      style={{ width: 480, maxWidth: '100%' }}
    >
      {nonCanceledOrders?.length > 0 && (
        <p>
          <b className="p-mt-0" style={{ color: 'var(--yellow-500)' }}>
            {t('WARNING')}: {t('The following orders cannot be cancelled.')}
            <br />
            {nonCanceledOrders?.map((o) => (
              <div key={'nonCancable_' + o?.id}>
                {' '}
                <b style={{ color: 'var(--yellow-600)' }}>{o?.seriski_broj}</b>
              </div>
            ))}
            <br />
            {ordersForCancel?.length > 0 && t('You can still cancel the rest.')}
          </b>
        </p>
      )}
      {ordersForCancel?.length > 0 && (
        <p>
          {ordersForCancel?.length > 1 ? (
            t('Are you sure you want to cancel this group of orders?')
          ) : (
            <>
              {t('Are you sure you want to cancel the following order?')}{' '}
              <b>{ordersForCancel[0]?.seriski_broj}</b>
            </>
          )}
          <br />
          <br />
          {t('This action cannot be undone.')}
        </p>
      )}
    </Dialog>
  );
}

export default CancelOrderDialog;
